import { Injectable } from '@angular/core'
import { QuoteSession } from '@app/models/quote-session.model'

declare global {
	interface Window {
		Stripe: any
	}
}

@Injectable({
	providedIn: 'root',
})
export class StripeService {
	private readonly stripePrimaryColor = '#511e78'
	private readonly stripeLoader = 'auto'
	private readonly stripeLayoutType = 'tabs'
	private readonly stripeElementType = 'payment'
	private readonly stripeShowEmail = 'never'
	private readonly stripeShowName = 'never'
	private readonly stripeElementId = '#stripeElement'
	private stripe!: Window['Stripe']
	private elements: any
	private clientSecret = ''
	private paymentMethodId = ''

	public createStripeElement(response: { quoteSession: QuoteSession }): void {
		if (!window.document.querySelector(this.stripeElementId)) return

		try {
			this.stripe = window.Stripe(
				response.quoteSession.paymentSessions &&
					response.quoteSession.paymentSessions[0]?.stripePublishableKey
			)
			this.clientSecret =
				(response.quoteSession?.paymentSessions &&
					response.quoteSession?.paymentSessions[0].stripeSetupIntentClientSecret) ||
				''

			if (!this.clientSecret) throw new Error('Client secret not provided')

			const appearance = {
				variables: {
					colorPrimary: this.stripePrimaryColor,
				},
			}

			this.elements = this.stripe.elements({
				appearance,
				loader: this.stripeLoader,
				clientSecret: this.clientSecret,
			})

			const stripeElement = this.elements.create(this.stripeElementType, {
				layout: {
					type: this.stripeLayoutType,
				},
				fields: {
					billingDetails: {
						email: this.stripeShowEmail,
						name: this.stripeShowName,
					},
				},
			})

			stripeElement.mount(this.stripeElementId)
		} catch (err) {
			console.log(err)
		}
	}

	public async confirmSetup({
		email,
		name,
	}: {
		email: string
		name: string
	}): Promise<string | undefined> {
		try {
			if (!this.paymentMethodId) {
				const { error: submitError } = await this.elements.submit()

				if (submitError) {
					throw submitError
				}

				const { error: setupIntentError, setupIntent } = await this.stripe.confirmSetup({
					elements: this.elements,
					redirect: 'if_required',
					clientSecret: this.clientSecret,
					confirmParams: {
						payment_method_data: {
							billing_details: {
								email,
								name,
							},
						},
					},
				})
				if (setupIntentError) {
					throw setupIntentError
				}

				this.paymentMethodId = setupIntent?.payment_method
			}

			return this.paymentMethodId
		} catch (error) {
			throw error
		}
	}

	public resetPaymentMethodId(): void {
		this.paymentMethodId = ''
	}
}
